import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import type { InferType } from 'yup'
import { array, object, string } from 'yup'

import {
    useCreateUserMutation,
    useGetUserByIdQuery,
    useUpdateUserMutation,
} from '../../services/tenant/users'
import { optionsRole } from '../../utils/selectsOptionsConst'
import Button from '../common/Button'
import Input, { InputStyles } from '../common/Input'
import Modal from '../common/Modal'
import SelectBox from '../common/SelectBox'
import SelectBoxAsynk from '../common/SelectBoxAsynk'
import { setApiError } from '@/utils/helpers'

const schemaAdd = object({
    fullName: string().required('Full Name is required'),
    roles: array()
        .of(string().defined())
        .min(1, 'At least one role is required')
        .required('Role is required'),
    email: string()
        .required('Email is required')
        .email('Please enter a valid email'),
    phone: string()
        .required('Phone is required')
        .matches(
            /^(\+?\d{1,3}[-.\s]?)?(\(?\d{1,4}\)?[-.\s]?)?\d{1,4}([-.\s]?\d{1,9})$/,
            'Phone number must be a valid format (e.g., +1234567890, (123) 456-7890)',
        ),
    password: string()
        .required('Password is required')
        .min(6, 'Password must be at least 6 characters')
        .max(30, 'Password cannot exceed 30 characters'),
})

const schemaEdit = object({
    fullName: string().required('Full Name is required'),
    roles: array()
        .of(string().defined())
        .min(1, 'At least one role is required')
        .required('Role is required'),
    email: string()
        .required('Email is required')
        .email('Please enter a valid email'),
    phone: string()
        .required('Phone is required')
        .matches(
            /^(\+?\d{1,3}[-.\s]?)?(\(?\d{1,4}\)?[-.\s]?)?\d{1,4}([-.\s]?\d{1,9})$/,
            'Phone number must be a valid format (e.g., +1234567890, (123) 456-7890)',
        ),
    password: string()
        .optional()
        .min(6, 'Password must be at least 6 characters')
        .max(30, 'Password cannot exceed 30 characters')
        .nullable()
        .transform(value => (value === '' ? null : value)),
})

type FormType = InferType<typeof schemaAdd>
type EditFormType = InferType<typeof schemaEdit>
interface AddServiceModalProps {
    isOpen: boolean
    onClose: () => void
    id: string | undefined | null
    setId?: React.Dispatch<React.SetStateAction<string | null>>
}
const AddUserModal = ({ isOpen, onClose, id, setId }: AddServiceModalProps) => {
    const { data: userData, isLoading: userLoading } = useGetUserByIdQuery(
        id as string,
        {
            skip: !id,
        },
    )
    const [updateUser, { isLoading: updateLoading }] = useUpdateUserMutation()
    const [createUser, { isLoading: createLoading }] = useCreateUserMutation()
    const {
        register,
        handleSubmit,
        control,
        setValue,
        reset,
        formState: { errors, isDirty },
        setError,
    } = useForm<FormType | EditFormType>({
        mode: 'onChange',
        resolver: yupResolver(id ? schemaEdit : schemaAdd),
    })

    useEffect(() => {
        if (userData?.data && id) {
            setValue('fullName', userData.data?.fullName)
            setValue('roles', userData.data?.roles)
            setValue('email', userData.data?.email || '')
            setValue('phone', userData.data?.phone || '')
        }
    }, [userData, id])

    const handelClose = () => {
        onClose()
        if (setId) setId(null)
    }

    const onSubmit = async (data: FormType | EditFormType) => {
        const formData = new FormData()

        Object.entries(data).forEach(([key, value]) => {
            if (key === 'roles') {
                value &&
                    (value as string[]).forEach(role =>
                        formData.append(`${key}[]`, role),
                    )
            } else {
                formData.append(key, String(value))
            }
        })
        if (data?.password === null || data?.password?.trim() === '') {
            formData.delete('password')
        }
        let res = null
        try {
            if (id) {
                res = await updateUser({ id: id, body: formData }).unwrap()
            } else {
                res = await createUser(formData).unwrap()
            }
            if (res?.success) {
                toast.success('Updated successfully!')
                reset()
                handelClose()
            } else {
                toast.error(res.message)
                setApiError(res?.errors, setError)
            }
        } catch (error) {
            toast.error('Something went wrong')
            console.error('rejected', error)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handelClose}
            title={`${id ? 'Edit' : 'Create'} User`}
            isLoading={userLoading}
        >
            <div className="modal__body p-0">
                <div className="custom-form">
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        autoComplete="off"
                    >
                        <div className="custom-form-body">
                            <div className="row gutters-16">
                                <div className="col-12">
                                    <Input
                                        inputStyle={InputStyles.outline}
                                        label="Name"
                                        placeholder="Enter Name..."
                                        {...register('fullName')}
                                        autoComplete="off"
                                        error={errors.fullName?.message}
                                    />
                                </div>
                                <div className="col-12">
                                    <Controller
                                        name="roles"
                                        control={control}
                                        render={({ field }) => (
                                            <SelectBoxAsynk
                                                wrapperClassName="input--lg select-secondary select-left-spase-0"
                                                label="Roles"
                                                options={optionsRole}
                                                fetchFunk={() => ({
                                                    options: optionsRole || [],
                                                    hasMore: false,
                                                    additional: {
                                                        page: 1,
                                                    },
                                                })}
                                                placeholder="Select roles..."
                                                error={errors.roles?.message}
                                                onChange={option =>
                                                    field.onChange(
                                                        option?.map(
                                                            (opt: any) =>
                                                                opt.value,
                                                        ),
                                                    )
                                                }
                                                isMultiple
                                                value={field.value?.map(
                                                    (val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }),
                                                )}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="col-12">
                                    <Input
                                        inputStyle={InputStyles.outline}
                                        label="Email"
                                        placeholder="Enter Email..."
                                        error={errors.email?.message}
                                        {...register('email')}
                                        autoComplete="new-email"
                                    />
                                </div>
                                <div className="col-12">
                                    <Input
                                        inputStyle={InputStyles.outline}
                                        error={errors.phone?.message}
                                        {...register('phone')}
                                        label="Phone Number"
                                        placeholder="Enter Phone Number..."
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-12">
                                    <Input
                                        inputStyle={InputStyles.outline}
                                        label="Password"
                                        type="password"
                                        placeholder="Enter Password..."
                                        error={errors.password?.message}
                                        {...register('password')}
                                        autoComplete="new-password"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="custom-form-footer justify-end">
                            <Button
                                type="submit"
                                size="lg"
                                variant="primary-gradient"
                                disabled={!isDirty}
                                loading={createLoading || updateLoading}
                            >
                                <span>
                                    {id ? 'Save Changes' : 'Create User'}
                                </span>
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    )
}

export default AddUserModal
